import { useApiQuery, useTeam } from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import { day } from '@vrplatform/utils';
import { Center } from '@mantine/core';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { FeeTypeCarousel } from '../../../../fees/overview/_components';
import type { FeeOverviewRoute } from '../../../../fees/overview/_types';
import { ListingDrawerFeeSections } from '../../../../fees/overview/listing-fee-drawer/ListingFeeDrawer';
import { getListingFeeSubscriptions } from '../../../../fees/overview/listing-fee-drawer/useListingFeeDrawerQuery';
import { getRecurringFeeEnumFromFeeTypeRoute } from '../../../../fees/overview/_utils';

export const ListingFeesTab = ({ listingId }: { listingId: string }) => {
  const feeType = useLocation().pathname.split('/')[4];
  const goto = useNavigate();

  const onFeeTypeChange = useCallback(
    (type: FeeOverviewRoute) =>
      goto(`/listing/${listingId}/fees-commissions/${type}`),
    [goto, listingId]
  );

  return (
    <>
      <FeeTypeCarousel
        feeType={feeType}
        onChange={onFeeTypeChange}
        listingId={listingId}
      />
      <Fees listingId={listingId} feeType={feeType as FeeOverviewRoute} />
    </>
  );
};

const Fees = ({
  listingId,
  feeType,
}: {
  listingId: string;
  feeType: FeeOverviewRoute;
}) => {
  const [{ id: teamId }] = useTeam();

  const queryData = useApiQuery(
    async (api, signal, args) => {
      return await getListingFeeSubscriptions(api, signal, {
        listingId: args.listingId,
        today: args.today,
        feeType: getRecurringFeeEnumFromFeeTypeRoute(args.feeType),
      });
    },
    {
      variables: {
        listingId,
        feeType,
        teamId,
        today: day().yyyymmdd(),
      },
      skip: !listingId || !feeType,
      keepPreviousData: true,
      queryGroup: 'listings',
      uniqueQueryKey: `listing-detail-view-fees-${feeType}`,
    }
  );

  if (!queryData.data)
    return (
      <Center mih={300}>
        <LoadingIndicator size="sm" />
      </Center>
    );

  return (
    <ListingDrawerFeeSections
      feeSubscriptions={queryData.data}
      isLoading={queryData.isFetching}
      listingId={listingId}
      feeType={feeType}
    />
  );
};
